import { useEffect, useState } from 'react';

/**
 * Colores de los gráficos de reportes (RES-47), leídos de las variables CSS del tema.
 *
 * Recharts pinta con atributos SVG (`fill`, `stroke`), que no resuelven `var(--x)`; por eso se
 * leen los valores computados del contenedor de la página y se vuelven a leer cuando cambia la
 * clase `dark` de `<html>` (la pone ThemeContext). Los tokens `--report-*` están definidos en
 * `InventoryReportsPage.css` con su paso claro y oscuro, validados con el validador de paletas de
 * dataviz contra el `--card-bg` de cada tema.
 */
const TOKENS = {
  series1: '--report-series-1',
  series2: '--report-series-2',
  surplus: '--report-surplus',
  shortage: '--report-shortage',
  grid: '--report-grid',
  axis: '--report-axis',
  surface: '--card-bg',
  text: '--text-primary',
};

// Sin estilos computados (jsdom) se cae a currentColor: el gráfico igual se dibuja.
const FALLBACK = Object.fromEntries(Object.keys(TOKENS).map((key) => [key, 'currentColor']));

const readColors = (node) => {
  const style = window.getComputedStyle(node);
  const next = {};
  Object.entries(TOKENS).forEach(([key, variable]) => {
    next[key] = style.getPropertyValue(variable).trim() || FALLBACK[key];
  });
  return next;
};

/**
 * @param {React.RefObject<HTMLElement>} ref - Contenedor donde viven los tokens
 * @returns {Object<string, string>} Colores `{ series1, series2, surplus, shortage, grid, axis, surface, text }`
 */
export default function useChartColors(ref) {
  const [colors, setColors] = useState(FALLBACK);

  useEffect(() => {
    const node = ref && ref.current;
    if (!node || typeof window.getComputedStyle !== 'function') return undefined;
    const update = () => {
      const next = readColors(node);
      setColors((prev) => (Object.keys(next).every((k) => prev[k] === next[k]) ? prev : next));
    };
    update();
    if (typeof MutationObserver !== 'function') return undefined;
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [ref]);

  return colors;
}
